/**
 * BottomSheet — Panel deslizable desde abajo. Patrón nativo móvil (Google Maps/Uber).
 * En desktop se comporta como modal centrado de ancho moderado.
 *
 * Props:
 *   open, onClose, title?, children, footer?
 *   maxHeight (default '90vh'), variant ('default'|'fullscreen')
 *   showHandle (default true) — barrita visual indicando "deslizable"
 */
import React, { useEffect } from 'react';
import { SwipeableDrawer, Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  maxHeight = '92vh',
  variant = 'default',
  showHandle = true,
  hideHeader = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // En desktop simulamos un modal centrado para no estirar el bottom sheet
  if (!isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            maxWidth: 480,
            mx: 'auto',
            borderTopLeftRadius: 16, borderTopRightRadius: 16,
            maxHeight, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {renderContent()}
      </SwipeableDrawer>
    );
  }

  function renderContent() {
    return (
      <>
        {showHandle && (
          <Box sx={{ pt: 1, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#cbd5e1' }} />
          </Box>
        )}
        {!hideHeader && title && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.25, borderBottom: '1px solid #e2e8f0',
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{title}</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        )}
        <Box sx={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </Box>
        {footer && (
          <Box sx={{ borderTop: '1px solid #e2e8f0', px: 2, py: 1.25, bgcolor: '#fff' }}>
            {footer}
          </Box>
        )}
      </>
    );
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          maxHeight, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          ...(variant === 'fullscreen' ? { height: '100%' } : {}),
        },
      }}
    >
      {renderContent()}
    </SwipeableDrawer>
  );
}
